import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {combineLatest, of, ReplaySubject} from 'rxjs';
import {Contract} from '../../../shared/interface/contract';
import {delay, distinctUntilChanged, first, skipUntil, startWith, takeUntil, tap} from 'rxjs/operators';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Uuid} from '../../../shared/interface/response/uuid';
import {HttpErrorResponse} from '@angular/common/http';
import {FormHelperService} from '../../../shared/services/form-helper.service';
import {ContractService} from '../../../shared/services/contract.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {
  COMMISSION_STATEMENT_GENERATION_CRITERIA_TYPE,
  CONTRACT_STATUS_ACTIVE,
  CONTRACT_STATUS_CANCELED,
  CONTRACT_STATUS_CLOSED,
  CONTRACT_STATUS_DURING_TERMINATION,
  CONTRACT_STATUS_SENT_TO_AGENT,
  CONTRACT_STATUS_SENT_WITH_AUTENTI,
  CONTRACT_STATUS_TERMINATED,
  CONTRACT_STATUS_TO_TERMINATE,
  CONTRACT_TYPE_AGREEMENT_FOR_PARTNER_POINTS,
  CONTRACT_TYPE_DURING_REGISTRATION,
  CONTRACT_TYPE_FOR_DEREGISTRATION,
  CONTRACT_TYPE_FRANCHISE,
  CONTRACT_TYPE_FRANCHISE_AGREEMENT,
  CONTRACT_TYPE_LEAD,
  CONTRACT_TYPE_LEAD_COMPLETE_PACKAGE,
  CONTRACT_TYPE_LEAD_ONLY_HEALTH,
  CONTRACT_TYPE_LEASING,
  CONTRACT_TYPE_MANDATE,
  CONTRACT_TYPE_MANDATE_COMPLETE_PACKAGE,
  CONTRACT_TYPE_MANDATE_ONLY_HEALTH, CONTRACT_TYPE_MANDATE_STUDENT,
  CONTRACT_TYPE_OCK,
  CONTRACT_TYPE_PARTNERSHIP,
  CONTRACT_TYPE_PROFIT,
  CONTRACT_TYPE_SELF_BILLING,
  CONTRACT_TYPE_TERMINAL,
  DICT_TYPE_TERMINATION_TIME,
  DictService,
  TERMINATION_TIME_IMMEDIATELY,
  TERMINATION_TIME_ONE_MONTH,
  TERMINATION_TIME_THREE_MONTHS,
  TERMINATION_TIME_TWO_MONTHS,
  TERMINATION_TYPE_BY_AGREEMENT,
  TERMINATION_TYPE_BY_SIDES_AGREEMENT
} from '../../../shared/services/dict.service';
import {PartnerService} from '../../../shared/services/partner.service';
import {OfwcaService} from '../../../shared/services/ofwca.service';
import {ListResponse} from '../../../shared/interface/response/list-response';
import {Dictionary} from '../../../shared/interface/dictionary';
import moment from 'moment';
import {MatDialog} from '@angular/material/dialog';
import {InfoDialogComponent} from '../../../shared/components/info-dialog/info-dialog.component';
import {validPhoneNumber} from '../../../shared/validator/phone-validator';
import {validIban} from '../../../shared/validator/iban-validator';
import {UserPermissions} from "../../../shared/interface/user-permissions";
import {AuthService} from "../../../shared/services/auth.service";
import {VatTaxpayers} from "../../../shared/interface/vat-taxpayers";
import {Partner} from "../../../shared/interface/partner";
import {CommissionTableListItem} from "../../../shared/interface/commission-table";
import {CommissionTableService} from "../../../shared/services/commission-table.service";

@Component({
  selector: 'app-contract-details-form',
  templateUrl: './contract-details-form.component.html',
  styleUrls: ['./contract-details-form.component.scss']
})
export class ContractDetailsFormComponent implements OnInit, OnDestroy {
  @Input() contract: ReplaySubject<Contract>;
  @Output() closeModification = new EventEmitter<any>();

  readonly CONTRACT_TYPE_PARTNERSHIP = CONTRACT_TYPE_PARTNERSHIP;
  readonly CONTRACT_TYPE_PROFIT = CONTRACT_TYPE_PROFIT;
  readonly CONTRACT_TYPE_SELF_BILLING = CONTRACT_TYPE_SELF_BILLING;
  readonly PARTNER_TYPES = [
    CONTRACT_TYPE_PARTNERSHIP,
    CONTRACT_TYPE_PROFIT,
    CONTRACT_TYPE_TERMINAL,
    CONTRACT_TYPE_FRANCHISE,
    CONTRACT_TYPE_OCK,
    CONTRACT_TYPE_LEASING,
    CONTRACT_TYPE_LEAD,
    CONTRACT_TYPE_SELF_BILLING,
    CONTRACT_TYPE_AGREEMENT_FOR_PARTNER_POINTS,
    CONTRACT_TYPE_FRANCHISE_AGREEMENT,
    CONTRACT_TYPE_MANDATE,
    CONTRACT_TYPE_MANDATE_ONLY_HEALTH,
    CONTRACT_TYPE_MANDATE_COMPLETE_PACKAGE,
    CONTRACT_TYPE_DURING_REGISTRATION,
    CONTRACT_TYPE_FOR_DEREGISTRATION,
    CONTRACT_TYPE_LEAD_ONLY_HEALTH,
    CONTRACT_TYPE_LEAD_COMPLETE_PACKAGE,
    CONTRACT_TYPE_MANDATE_STUDENT
  ];
  readonly OFWCA_TYPES = [
    CONTRACT_TYPE_MANDATE,
    CONTRACT_TYPE_MANDATE_ONLY_HEALTH,
    CONTRACT_TYPE_MANDATE_COMPLETE_PACKAGE,
    CONTRACT_TYPE_DURING_REGISTRATION,
    CONTRACT_TYPE_FOR_DEREGISTRATION,
    CONTRACT_TYPE_LEAD,
    CONTRACT_TYPE_LEAD_ONLY_HEALTH,
    CONTRACT_TYPE_LEAD_COMPLETE_PACKAGE,
    CONTRACT_TYPE_MANDATE_STUDENT
  ];
  readonly CUSTOM_TERMS_CONTRACT_TYPES = [
    CONTRACT_TYPE_PARTNERSHIP,
    CONTRACT_TYPE_PROFIT,
    CONTRACT_TYPE_MANDATE,
    CONTRACT_TYPE_MANDATE_ONLY_HEALTH,
    CONTRACT_TYPE_MANDATE_COMPLETE_PACKAGE,
    CONTRACT_TYPE_MANDATE_STUDENT
  ];
  readonly CONTRACT_STATUS_TO_TERMINATE = CONTRACT_STATUS_TO_TERMINATE;
  readonly CONTRACT_STATUS_DURING_TERMINATION = CONTRACT_STATUS_DURING_TERMINATION;
  readonly CONTRACT_STATUS_TERMINATED = CONTRACT_STATUS_TERMINATED;
  readonly CONTRACT_STATUS_CLOSED = CONTRACT_STATUS_CLOSED;
  readonly CONTRACT_TYPE_LEAD_ONLY_HEALTH = CONTRACT_TYPE_LEAD_ONLY_HEALTH;
  readonly CONTRACT_TYPE_LEAD_COMPLETE_PACKAGE = CONTRACT_TYPE_LEAD_COMPLETE_PACKAGE;

  formErrors = {};
  contractObject?: Contract = null;
  processingRequest = false;
  enableSaveButton = false;
  showPartnershipContractSelect = false;
  contractForm: FormGroup;
  typeControl: FormControl;
  concludeDateControl: FormControl;
  companyControl: FormControl;
  activeFromControl: FormControl;
  partnerControl: FormControl;
  activeToControl: FormControl;
  periodControl: FormControl;
  terminationTimeControl: FormControl;
  terminationTypeControl: FormControl;
  statusControl: FormControl;
  parentContractControl: FormControl;
  cancellationReasonControl: FormControl;
  selfBillingEmailControl: FormControl;
  selfBillingPhoneControl: FormControl;
  selfBillingAccountNumberControl: FormControl;
  selfBillingVatPayerControl: FormControl;
  selfBillingActiveVatPayerControl: FormControl;
  selfBillingCheckoutMethodControl: FormControl;
  selfBillingExpectsControl: FormControl;
  automaticBillingControl: FormControl;
  selfBillingExpectsDateControl: FormControl;
  customRatesForm: FormControl;
  customTermsForm: FormControl;
  facilityControl: FormControl;
  assignedPartnerControl: FormControl;
  assignedContractControl: FormControl;
  commissionTableControl: FormControl;
  commissionTables: CommissionTableListItem[] = [];
  commissionTableRequired = false;
  contractType?: Dictionary;
  terminationStatus = false;
  isCustomCommission = false;
  requiredActiveTo = false;
  requireCancellationFields = false;
  // getListOfVatButton = false;
  partnerNip: string;
  vatTaxpayersAccountNumber = [];
  contracts: ReplaySubject<Contract[]> = new ReplaySubject<Contract[]>(1);

  userPermissions: ReplaySubject<UserPermissions>;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private initialized$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private fb: FormBuilder,
    private formHelper: FormHelperService,
    private contractService: ContractService,
    private snack: MatSnackBar,
    private dictService: DictService,
    private partnerService: PartnerService,
    private ofwcaService: OfwcaService,
    private dialog: MatDialog,
    private auth: AuthService,
    private commissionTableService: CommissionTableService

  ) {
    this.userPermissions = auth.getUserPermissions();
    this.assignedContractControl = fb.control(false, []);
    this.contractForm = fb.group({
      type: this.typeControl = fb.control('', [Validators.required]),
      concludeDate: this.concludeDateControl = fb.control('', [Validators.required]),
      company: this.companyControl = fb.control('', [Validators.required]),
      commissionTable: this.commissionTableControl = fb.control( '', null),
      activeFrom: this.activeFromControl = fb.control(''),
      activeTo: this.activeToControl = fb.control(''),
      period: this.periodControl = fb.control('', [Validators.required]),
      terminationTime: this.terminationTimeControl = fb.control('', [Validators.required]),
      terminationType: this.terminationTypeControl = fb.control('', [Validators.required]),
      status: this.statusControl = fb.control(''),
      parentContract: this.parentContractControl = fb.control(''),
      partner: this.partnerControl = fb.control(''),
      verified: fb.control(true),
      note: fb.control(''),
      facility: this.facilityControl = fb.control(''),
      cancellationReason: this.cancellationReasonControl = fb.control('', [Validators.required]),
      cancellationExplanation: fb.control(''),
      terminationReason: fb.control(''),
      number: fb.control('', [Validators.required]),
      selfBillingEmail: this.selfBillingEmailControl = fb.control('', [Validators.required, Validators.email]),
      selfBillingPhone: this.selfBillingPhoneControl = fb.control('', [Validators.required, validPhoneNumber()]),
      selfBillingAccountNumber: this.selfBillingAccountNumberControl = fb.control('', [Validators.required, validIban()]),
      selfBillingVatPayer: this.selfBillingVatPayerControl = fb.control(false),
      selfBillingActiveVatPayer: this.selfBillingActiveVatPayerControl = fb.control(false),
      selfBillingCheckoutMethod: this.selfBillingCheckoutMethodControl = fb.control(false),
      invoiceServiceDate: fb.control(''),
      automaticBilling: this.automaticBillingControl =  fb.control(false),
      selfBillingExpects: this.selfBillingExpectsControl =  fb.control(false),
      selfBillingExpectsDate: this.selfBillingExpectsDateControl =  fb.control(false),
      customRates: this.customRatesForm = fb.control(false),
      customTerms: this.customTermsForm = fb.control(false),
      newContractTemplate: fb.control(''),
      assignedPartner: this.assignedPartnerControl = fb.control(''),
    });
  }

  ngOnInit(): void {
    this.commissionTableService
      .getOptions()
      .pipe(
        first(),
        takeUntil(this.destroyed$)
      )
      .subscribe(
        (
          commissionTables:
          CommissionTableListItem[]
        ) => {
          this.commissionTables =
            commissionTables.sort(
              (
                a: CommissionTableListItem,
                b: CommissionTableListItem
              ) =>
                (b.validFrom || '')
                  .localeCompare(
                    a.validFrom || ''
                  )
            );
        }
      );
    this.contractForm.valueChanges
      .pipe(
        delay(0),
        tap(() => this.enableSaveButton = this.contractForm.valid),
        takeUntil(this.destroyed$)
      )
      .subscribe();

    this.assignedContractControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe(val => {
      if (val){
        this.assignedPartnerControl.setValidators([Validators.required]);
      } else  {
        this.assignedPartnerControl.setValidators([]);
        this.assignedPartnerControl.reset();
      }
    });

    this.contract
      .pipe(takeUntil(this.destroyed$))
      .subscribe((contract: Contract) => {
        this.contractObject = contract;
        this.initialize(contract);
      });

    this.statusControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe(dictId => {
        this.dictService.getKey(dictId)
          .subscribe((dictKey: string) => {
            this.terminationStatus =
              [
                CONTRACT_STATUS_DURING_TERMINATION,
                CONTRACT_STATUS_TERMINATED,
              ].includes(
                dictKey
              );

            if (this.terminationStatus) {
              this.terminationTypeControl.enable();
              this.terminationTimeControl.enable();
            } else {
              this.terminationTypeControl.disable();
              this.terminationTypeControl.reset();
              this.terminationTimeControl.disable();
              this.terminationTimeControl.reset();
              this.activeToControl.reset();
            }

            if (
              CONTRACT_STATUS_ACTIVE === dictKey
              && !this.activeFromControl.value
            ) {
              this.activeFromControl.setValue(new Date().toISOString().split('T')[0]);
            }

            if (
              CONTRACT_STATUS_ACTIVE === dictKey
            ) {
              this.selfBillingAccountNumberControl.setValidators([Validators.required, validIban()]);
              this.selfBillingAccountNumberControl.updateValueAndValidity();
            } else {
              this.selfBillingAccountNumberControl.setValidators([]);
              this.selfBillingAccountNumberControl.updateValueAndValidity();
            }

            if (CONTRACT_STATUS_CANCELED === dictKey) {
              this.requireCancellationFields = true;
              this.cancellationReasonControl.enable();
            } else {
              this.requireCancellationFields = false;
              this.cancellationReasonControl.disable();
            }
            if (CONTRACT_STATUS_TERMINATED === dictKey || CONTRACT_STATUS_DURING_TERMINATION === dictKey) {
              this.requiredActiveTo = true;
              this.activeToControl.setValidators([Validators.required]);
            } else {
              this.requiredActiveTo = false;
              this.activeToControl.setValidators([]);

            }
          });
      });

    this.statusControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        skipUntil(this.initialized$),
        distinctUntilChanged()
      ).subscribe((dictId: number) => {
        this.dictService.getKey(dictId)
          .subscribe((dictKey: string) => {
            if (CONTRACT_STATUS_TERMINATED === dictKey) {
              this.dialog.open(InfoDialogComponent, {
                data: {
                  text: 'Zmiana statusu umowy na "Rozwiązana" spowoduje zmianę statusów rejestracji wszystkich OFWCA na "Do zablokowania"'
                },
                width: '50vw',
              });
            }

            if (
              [CONTRACT_STATUS_SENT_WITH_AUTENTI, CONTRACT_STATUS_SENT_TO_AGENT].includes(dictKey) &&
              this.contractType?.key === CONTRACT_TYPE_SELF_BILLING
            ) {
              this.concludeDateControl.setValue(moment());
            }
          });
      });

    this.typeControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((dictId: number) => {
        this.dictService.getKey(dictId)
          .subscribe((dictKey: string) => {
            this.showPartnershipContractSelect = [CONTRACT_TYPE_TERMINAL, CONTRACT_TYPE_SELF_BILLING].includes(dictKey);
          });
      });

    this.selfBillingVatPayerControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        startWith(false)
      ).subscribe((vatPayer: boolean) => {
      if (vatPayer) {
        this.selfBillingCheckoutMethodControl.enable();
      } else {
        this.selfBillingCheckoutMethodControl.setValue(false);
        this.selfBillingCheckoutMethodControl.disable();
      }
    });

    combineLatest([
      this.companyControl.valueChanges.pipe(startWith(this.companyControl.value)),
      this.typeControl.valueChanges.pipe(startWith(this.typeControl.value)),
      this.concludeDateControl.valueChanges.pipe(startWith(this.concludeDateControl.value)),
    ])
      .pipe(
        skipUntil(this.initialized$),
        takeUntil(this.destroyed$),
        delay(0)
      )
      .subscribe(() => {
        // this.setDefaultCommissionTable();
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  contractTypeChanged(contractType: Dictionary): void {
    this.contractType = contractType;
    // this.updateCommissionTableRequirement();
    if (this.contractType?.key === this.CONTRACT_TYPE_SELF_BILLING || this.contractType?.key === CONTRACT_TYPE_PARTNERSHIP || this.contractType?.key === CONTRACT_TYPE_PROFIT) {
      this.selfBillingEmailControl.enable();
      this.selfBillingPhoneControl.enable();
      this.selfBillingAccountNumberControl.enable();
    } else {
      this.selfBillingEmailControl.disable();
      this.selfBillingPhoneControl.disable();
      this.selfBillingAccountNumberControl.disable();
    }
    if (this.contractType?.key === CONTRACT_TYPE_FRANCHISE ||
      this.contractType?.key === CONTRACT_TYPE_AGREEMENT_FOR_PARTNER_POINTS ||
      this.contractType?.key === CONTRACT_TYPE_FRANCHISE_AGREEMENT) {
      this.facilityControl.setValidators([Validators.required]);
      this.facilityControl.enable();
    } else {
      this.facilityControl.clearValidators();
      this.facilityControl.disable();
    }
  }

  initialize(contract: Contract): void {
    of(contract)
      .pipe(
        delay(0),
        tap(() => {
          this.isCustomCommission = contract.automaticBilling;
          }),
        tap(() => {
          this.contractForm.reset({
            type: contract.type?.id,
            concludeDate: contract.concludeDate,
            company: contract.company?.id,
            commissionTable: contract.commissionTable?.uuid ?? null,
            activeFrom: contract.activeFrom,
            activeTo: contract.activeTo,
            period: contract.period?.id,
            terminationTime: contract.terminationTime?.id,
            terminationType: contract.terminationType?.id,
            status: contract.status?.id,
            verified: contract.verified,
            note: contract.note,
            cancellationReason: contract.cancellationReason?.id,
            cancellationExplanation: contract.cancellationExplanation,
            number: contract.number,
            selfBillingEmail: contract.selfBillingEmail,
            selfBillingPhone: contract.selfBillingPhone,
            selfBillingAccountNumber: contract.selfBillingAccountNumber,
            selfBillingVatPayer: contract.selfBillingVatPayer,
            selfBillingCheckoutMethod: contract.selfBillingCheckoutMethod,
            partner: contract.partner?.uuid,
            facility: contract.facility?.id,
            terminationReason: contract?.terminationReason,
            selfBillingActiveVatPayer: contract.selfBillingActiveVatPayer,
            invoiceServiceDate: contract.invoiceServiceDate,
            automaticBilling: !!contract.automaticBilling,
            selfBillingExpects: !!contract.selfBillingExpects,
            selfBillingExpectsDate: contract.selfBillingExpects,
            customRates: contract.customRates,
            customTerms: contract.customTerms,
            newContractTemplate: contract.newContractTemplate,
            assignedPartner: contract.assignedPartner?.uuid,
          });
          this.contractTypeChanged(contract.type);
          this.partnerNip = contract.partner?.nip;
          if (contract.assignedPartner)
          {
            this.assignedContractControl.setValue(true);
          }else {
            this.assignedContractControl.setValue(false);
          }
        }),
        tap(() => {
          if (contract.partner) {
            this.partnerService.getPartnerContracts(contract.partner.uuid)
              .subscribe((contracts: ListResponse<Contract>) => {
                const availableContracts = contracts.items.filter((item: Contract) =>
                  contract.company?.id === item.company?.id &&
                  [CONTRACT_TYPE_PARTNERSHIP, CONTRACT_TYPE_PROFIT].includes(item.type?.key)
                );
                this.contracts.next(availableContracts);
                if (!contract.selfBillingEmail || !contract.selfBillingPhone || !contract.selfBillingAccountNumber) {
                  this.fillSettlementDataForCooperationContract(contract, contracts.items);
                }
              });
          }

          if (contract.ofwca) {
            this.ofwcaService.getOfwcaContracts(contract.ofwca.uuid)
              .subscribe((contracts: ListResponse<Contract>) => {
                const availableContracts = contracts.items.filter((item: Contract) =>
                  contract.company?.id === item.company?.id &&
                  [CONTRACT_TYPE_PARTNERSHIP, CONTRACT_TYPE_PROFIT].includes(item.type?.key)
                );
                this.contracts.next(availableContracts);
              });
          }
        }),
        delay(0),
        tap(() => this.parentContractControl.reset(contract.parentContract?.uuid)),
        delay(0),
        tap(() => {
          this.contractForm.markAllAsTouched();
          this.initialized$.next(true);
        })
      ).subscribe();
  }

  sendForm(): void {
    if (!this.contractForm.valid) {
      this.contractForm.markAllAsTouched();
      this.snack.open('Wypełnij lub popraw wszystkie wymagane pola', '', {
        duration: 2000,
      });
      return;
    }

    if (this.selfBillingActiveVatPayerControl.value  && this.contractType?.key !== CONTRACT_TYPE_PARTNERSHIP){
      if (!this.vatTaxpayersAccountNumber.includes(this.selfBillingAccountNumberControl.value.substr(2).replace(/ /g, ""))){
        this.selfBillingAccountNumberControl.setErrors({vatPayerControl: true});
        this.snack.open('Numer rachunku bankowego jest inny niż w rejestrze płatników VAT', '', {
          duration: 2000,
        });
        return;
      }
    }

    this.processingRequest = true;
    this.formHelper.removeBackendErrorState(this.contractForm, this.formErrors);
    this.formErrors = {};

    this.contractService.sendForm(this.contractForm.getRawValue(), this.contractObject.uuid)
      .pipe(
        first()
      )
      .subscribe((response: Uuid) => {
        this.snack.open('Pomyślnie zaktualizowano obiekt umowy', '', {
          duration: 2000,
        });
        this.processingRequest = false;
        this.closeModification.emit();
      }, (error: HttpErrorResponse) => {
        this.processingRequest = false;
        this.snack.open('Podczas aktualizacji obiektu umowy wystąpił błąd', error.status.toString(), {
          duration: 2000,
        });

        if (error.status === 400) {
          this.formErrors = error.error.error.list;
          this.formHelper.setBackendErrorState(this.contractForm, this.formErrors);
          this.snack.open(error.error.error.text, error.status.toString(), {
            duration: 4000,
          });
        }
      })
    ;
  }

  cancel(): void {
    this.closeModification.emit();
  }

  onTerminationTimeChange(terminationTime: Dictionary): void {
    const activeToDate = moment();
    switch (terminationTime.key) {
      case TERMINATION_TIME_ONE_MONTH:
        activeToDate.add(1, 'months');
        activeToDate.endOf('month');
        break;
      case TERMINATION_TIME_TWO_MONTHS:
        activeToDate.add(2, 'months');
        activeToDate.endOf('month');
        break;
      case TERMINATION_TIME_THREE_MONTHS:
        activeToDate.add(3, 'months');
        activeToDate.endOf('month');
        break;
      default:
    }

    this.activeToControl.setValue(activeToDate);
  }

  onTerminationTypeChange(terminationType: Dictionary): void {
    if ([TERMINATION_TYPE_BY_SIDES_AGREEMENT, TERMINATION_TYPE_BY_AGREEMENT].includes(terminationType.key)) {
      this.dictService.get(TERMINATION_TIME_IMMEDIATELY, DICT_TYPE_TERMINATION_TIME)
        .subscribe((dict: Dictionary) => {
          this.terminationTimeControl.setValue(dict.id);
          this.onTerminationTimeChange(dict);
        });
    }
  }

  // getListOfVat(): void {
  //   this.getListOfVatButton = false;
  //   this.snack.open('Pobieranie danych z wykazu podatników VAT');
  //   this.contractService.listOfVatInformation(this.partnerNip )
  //     .subscribe((e: VatTaxpayers) => {
  //       this.snack.open('Pobrano dane z wykazu podatników VAT','' , {duration: 2000});
  //       this.contractForm.get('selfBillingVatPayer').setValue(e.vatPayer);
  //       this.contractForm.get('selfBillingActiveVatPayer').setValue(e.activeVatPayer);
  //       this.vatTaxpayersAccountNumber = e.accountNumbers;
  //       this.getListOfVatButton = true;
  //     }, (error: HttpErrorResponse) => {
  //       this.snack.open('Nie udało się pobrać danych z  wykazu podatników VAT', error.status.toString(), {
  //         duration: 2000
  //       });
  //     });
  //   this.selfBillingVatPayerControl.disable();
  //   this.selfBillingActiveVatPayerControl.disable();
  //
  // }

  copyMessage(val: string): void{
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = val;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
    this.snack.open('Skopiowano do schowka', '', {duration: 2000});

  }

  private isCooperationContract(contract: Contract): boolean {
    return [CONTRACT_TYPE_PARTNERSHIP, CONTRACT_TYPE_PROFIT].includes(contract?.type?.key);
  }

  private isActiveSelfBillingContract(contract: Contract): boolean {
    return contract?.type?.key === CONTRACT_TYPE_SELF_BILLING
      && contract?.status?.key === CONTRACT_STATUS_ACTIVE;
  }

  private getPartnerBillingEmail(partner: Partner): string {
    if (partner?.billingEmails?.length) {
      return partner?.billingEmails?.[0]?.address || '';
    }
      return partner?.mainEmails?.[0]?.address || '';

  }

  private getPartnerBillingPhone(partner: Partner): string {
    if (partner?.billingPhones?.length) {
      return partner?.billingPhones?.[0]?.number || '';
    }
    return partner?.primaryPhones?.[0]?.number || '';
  }

  private getPartnerBillingAccountNumber(partner: Partner): string {
    return partner?.accountNumber || '';
  }

  private applySettlementData(email?: string, phone?: string, accountNumber?: string): void {
    if (!this.selfBillingEmailControl.value){
      this.selfBillingEmailControl.setValue(email || '');
    }
    if (!this.selfBillingPhoneControl.value) {
      this.selfBillingPhoneControl.setValue(phone || '');
    }
    if (!this.selfBillingAccountNumberControl.value){
      this.selfBillingAccountNumberControl.setValue(accountNumber || '');
    }
  }

  private fillSettlementDataFromPartner(partner: Partner): void {
    this.applySettlementData(
      this.getPartnerBillingEmail(partner),
      this.getPartnerBillingPhone(partner),
      this.getPartnerBillingAccountNumber(partner),
    );
  }

  private fillSettlementDataForCooperationContract(
    editedContract: Contract,
    contracts: Contract[],
  ): void {
    if (!this.isCooperationContract(editedContract) || !editedContract?.partner) {
      return;
    }

    const activeSelfBillingContract = contracts.find((item: Contract) =>
      this.isActiveSelfBillingContract(item)
    );


    if (activeSelfBillingContract) {
      this.applySettlementData(
        activeSelfBillingContract.selfBillingEmail,
        activeSelfBillingContract.selfBillingPhone,
        activeSelfBillingContract.selfBillingAccountNumber,
      );
      return;
    }

    this.fillSettlementDataFromPartner(editedContract.partner);
  }


  private updateCommissionTableRequirement(): void {
    this.commissionTableRequired =
      COMMISSION_STATEMENT_GENERATION_CRITERIA_TYPE
        .includes(
          this.contractType?.key
        );

    if (this.commissionTableRequired) {
      this.commissionTableControl.setValidators([
        Validators.required
      ]);
    } else {
      this.commissionTableControl.clearValidators();

      this.commissionTableControl.setValue(null,
        {
          emitEvent: false
        }
      );
    }

    this.commissionTableControl.updateValueAndValidity({
      emitEvent: false
    });
  }
  private setDefaultCommissionTable(): void {
    if (!this.commissionTableRequired) {
      return;
    }

    /*
     * Zmiana parametrów umowy unieważnia
     * dotychczasowy wybór tabeli.
     */
    this.commissionTableControl.setValue(null, {emitEvent: false});

    const companyId: number = this.companyControl.value;
    const contractTypeId: number = this.typeControl.value;

    if (
      !companyId
      || !contractTypeId
      || !this.commissionTables.length
    ) {
      return;
    }

    const matchingTables =
      this.commissionTables
        .filter((table: CommissionTableListItem) => (table.assignments || []).some(assignment => Number(assignment.companyId) === Number(companyId) && Number(assignment.contractTypeId) === Number(contractTypeId)))
        .sort((a: CommissionTableListItem, b: CommissionTableListItem) => (b.validFrom || '').localeCompare(a.validFrom || ''));

    if (!matchingTables.length) {
      return;
    }

    const concludeDate = this.concludeDateControl.value;

    /*
     * Brak daty:
     * wybieramy ostatni standard przypisany
     * do Firma + Rodzaj umowy.
     */
    if (!concludeDate) {
      this.commissionTableControl.setValue(matchingTables[0].uuid);

      return;
    }

    const date = moment(concludeDate).format('YYYY-MM-DD');
    const commissionTable = matchingTables.find((table: CommissionTableListItem) => table.validFrom <= date && (!table.validTo || table.validTo >= date));

    this.commissionTableControl.setValue(commissionTable?.uuid ?? null);
  }
}
